import { ref, computed } from 'vue';
import { membersApi } from '@/entities/Branch';
import { toastSuccess, toastError, useCustomToast } from '@/shared/lib';
import { useMemberPositionForm } from './useMemberPositionForm';
import { useEligibleUserSearch } from './useEligibleUserSearch';

export function useCreateMember() {
  const toast = useCustomToast();
  const form = useMemberPositionForm();
  const userSearch = useEligibleUserSearch();

  const isSaving = ref(false);

  const isFormValid = computed(
    () => userSearch.selectedUser.value !== null && form.completePairs.value.length >= 1
  );

  async function save(): Promise<{ success: boolean }> {
    if (!isFormValid.value || !userSearch.selectedUser.value || isSaving.value) return { success: false };

    isSaving.value = true;
    try {
      const positionIds = form.completePairs.value.map((p) => p.positionId as number);
      const functionalActivityIds = form.functionalActivitySlots.value
        .map((s) => s.id)
        .filter((id): id is number => id !== null);

      await membersApi.createMember({
        userId: userSearch.selectedUser.value.id,
        positionIds,
        ...(functionalActivityIds.length > 0 ? { functionalActivityIds } : {}),
      });

      toastSuccess(toast, 'Membro criado com sucesso');
      return { success: true };
    } catch {
      toastError(toast, 'Erro ao criar Membro, tente novamente.');
      return { success: false };
    } finally {
      isSaving.value = false;
    }
  }

  function reset() {
    userSearch.reset();
    form.resetForm();
  }

  async function initialize() {
    await Promise.all([
      form.loadDepartments(),
      form.loadFunctionalActivities(),
      userSearch.loadInitialUsers(),
    ]);
  }

  return {
    ...form,
    ...userSearch,
    isSaving,
    isFormValid,
    save,
    reset,
    initialize,
  };
}
